/**
 * Which review stays selected when the queue reloads.
 *
 * Pure on purpose — `verify_queue_selection.ts` proves it without a DOM.
 */

/**
 * Why the list reloaded. This is the whole rule: the same reload means
 * different things depending on what the person just did.
 *
 * - `initial`  — first load of this screen.
 * - `filter`   — they changed a filter, window, store or sort. They are AIMING
 *                at something, so the selection must never be overridden.
 * - `mutation` — they approved, skipped, snoozed or hid a review. They are
 *                FINISHING one, so the next piece of work should be waiting.
 */
export type ReloadReason = "initial" | "filter" | "mutation";

/**
 * Pick the selection for a freshly loaded list.
 *
 * The bug this replaces: the queue re-ran `setSelectedId(visibleIds[0])` after
 * every reload where the current selection had dropped out. Flagged reviews sort
 * to position 1, so every filter click handed the same 1-star review straight
 * back — which reads as "the filters don't do anything", because the thing on
 * screen never changes no matter what you click.
 *
 * On a filter change this can only ever return the previous selection or null.
 * It never introduces a review the person did not already have open.
 */
export function nextSelection(args: {
  previous: string | null;
  visibleIds: string[];
  reason: ReloadReason;
}): string | null {
  const { previous, visibleIds, reason } = args;
  const stillVisible = previous !== null && visibleIds.includes(previous);
  if (stillVisible) return previous;

  switch (reason) {
    // Nothing is open yet, so opening the top row is a help, not an override.
    case "initial":
      return visibleIds[0] ?? null;
    // They aimed somewhere. If their review didn't survive the filter, say so by
    // clearing the pane — do not quietly substitute a different one.
    case "filter":
      return null;
    // The row they were working on left the queue because they finished it.
    // Hand them the next one.
    case "mutation":
      return visibleIds[0] ?? null;
  }
}
